import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Chip,
  Card,
  CardContent,
} from '@mui/material';
import { Delete as DeleteIcon } from '@mui/icons-material';
import { memoryService } from '../services/memoryService';
import { useNotificationStore } from '../store/notificationStore';
import { PageDetailLayout } from '../components/layouts/PageDetailLayout';
import { MemoryScope, MemoryType, type AgentMemoryEntryDto } from '../types/memory';

const scopeLabel = (scope: number) => {
  switch (scope) {
    case MemoryScope.Session:
      return 'Session';
    case MemoryScope.Project:
      return 'Project';
    case MemoryScope.Tenant:
      return 'Tenant';
    default:
      return 'Unknown';
  }
};

const typeLabel = (type: number) => {
  switch (type) {
    case MemoryType.Fact:
      return 'Fact';
    case MemoryType.Decision:
      return 'Decision';
    case MemoryType.Outcome:
      return 'Outcome';
    case MemoryType.Preference:
      return 'Preference';
    default:
      return 'Unknown';
  }
};

/**
 * Memory detail page component.
 */
export const MemoryDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { addNotification } = useNotificationStore();

  const [memory, setMemory] = useState<AgentMemoryEntryDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const load = async () => {
      if (!id) return;
      setLoading(true);
      setError(null);
      try {
        const m = await memoryService.getMemory(id);
        setMemory(m);
      } catch (err: any) {
        const msg = err.response?.data?.message || 'Failed to load memory';
        setError(msg);
        addNotification(msg, 'error');
      } finally {
        setLoading(false);
      }
    };

    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleDelete = async () => {
    if (!id) return;
    if (!window.confirm('Delete this memory entry?')) return;
    try {
      await memoryService.deleteMemory(id);
      addNotification('Memory deleted', 'success');
      navigate('/memories');
    } catch (err: any) {
      const msg = err.response?.data?.message || 'Failed to delete memory';
      addNotification(msg, 'error');
    }
  };

  return (
    <PageDetailLayout
      title="Memory Details"
      breadcrumbs={[{ label: 'Memories', href: '/memories' }, { label: 'Memory Details' }]}
      backTo={{ label: 'Memories', href: '/memories' }}
      loading={loading}
      error={error || undefined}
      onErrorDismiss={() => setError(null)}
      chips={memory ? [
        { label: `Scope: ${scopeLabel(memory.scope)}` },
        { label: `Type: ${typeLabel(memory.type)}` },
        { label: `Importance: ${memory.importanceScore.toFixed(2)}`, color: 'primary' as const },
        { label: `Accesses: ${memory.accessCount}`, variant: 'outlined' as const },
        ...(memory.expiresAt ? [{ label: `Expires: ${new Date(memory.expiresAt).toLocaleString()}`, color: 'warning' as const }] : []),
      ] : []}
      actions={memory ? [
        { icon: <DeleteIcon />, label: 'Delete Memory', onClick: handleDelete, color: 'error' as const },
      ] : []}
    >
      {memory && (
        <Card>
          <CardContent>
            {/* Summary Section */}
            {memory.summary && (
              <Box sx={{ mb: 4 }}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, fontWeight: 600 }}>
                  Summary
                </Typography>
                <Typography variant="body1">{memory.summary}</Typography>
              </Box>
            )}

            {/* Content Section */}
            <Box sx={{ mb: 4 }}>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, fontWeight: 600 }}>
                Content
              </Typography>
              <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                {memory.content}
              </Typography>
            </Box>

            {/* Tags Section */}
            {memory.tags && memory.tags.length > 0 && (
              <Box sx={{ mb: 4 }}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, fontWeight: 600 }}>
                  Tags
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {memory.tags.map((t) => (
                    <Chip key={t} label={t} size="small" />
                  ))}
                </Box>
              </Box>
            )}

            {/* Metadata Section */}
            {memory.metadataJson && (
              <Box>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, fontWeight: 600 }}>
                  Metadata (JSON)
                </Typography>
                <Card variant="outlined" sx={{ p: 3, backgroundColor: 'background.default' }}>
                  <Typography
                    component="pre"
                    sx={{
                      whiteSpace: 'pre-wrap',
                      m: 0,
                      fontFamily: 'monospace',
                      fontSize: '0.875rem',
                      overflow: 'auto',
                    }}
                  >
                    {memory.metadataJson}
                  </Typography>
                </Card>
              </Box>
            )}
          </CardContent>
        </Card>
      )}
    </PageDetailLayout>
  );
};
